import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, InputDate, InputText, Table, Text } from "@pentabd/ui";

import "../../../style.scss";

import { DeleteConfirmationModal, ErrorModal, SuccessModal } from "@components";
import { IconCalendar, IconSearch, IconXClose } from "@pentabd/icons";

import { Pagination } from "@type/taskManagement/pagination";
import { useNavigate } from "react-router-dom";
import useActionCreator from "@redux/hooks/useActionCreator";
import { useAllTasksList } from "@hooks/taskManagement/allTasks/useAllTasksList";
import { AllTasksResponseType } from "@type/taskManagement/allTasks";
import { SelectControllerOptions } from "@components/SelectControllerOptions";
import { useStatusList } from "@hooks/taskManagement/settings/status/useStatusList";
import { StatusResponseType } from "@type/taskManagement/settings/status";
import { StatusOptions } from "@type/taskManagement/common";
import ArchivedTasksListTableColumn from "./ArchivedTasksListTableColumn";
import DropdownTreeSelect from "react-dropdown-tree-select";
import { unitTreeDataConvert } from "@helpers/unitTreeDataConvert";
import { useBaseData } from "@hooks/useBaseData";

import "../../../style.scss";
import { BASE_DATA } from "@constants/base-data";
import { useLanguage } from "@hooks/useLanguage";
const { UNIT_TREE } = BASE_DATA;

export const ArchivedTasksListTable = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { language } = useLanguage();

  const [statusOptions, setStatusOptions] = useState<StatusOptions>([
    { label: "All", value: "all" },
  ]);

  const { data: unitTree } = useBaseData(UNIT_TREE);

  const [unitForSearch, setUnitForSearch] = useState<string>("");

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false);
  const [itemId, setItemId] = useState<number>();
  const [itemName, setItemName] = useState<string>("");

  const { handleQuery, allTasksState, clearSearch, hasFilter, query } =
    useAllTasksList(true);

  const { statusState } = useStatusList();
  const { getData: statusGetData } = statusState;
  const { data: statusData } = statusGetData || ({} as StatusResponseType);

  const [formMethods, setFormMethods] = useState<any>(null);

  const { taskManagementActionsHandlers } = useActionCreator();

  const { allTasksDeleteActionHandler } = taskManagementActionsHandlers;

  const { getData, deleteSuccess, deleteError } = allTasksState;

  const { data, pagination } = getData || ({} as AllTasksResponseType);

  const { page, total, size } = pagination || ({} as Pagination);

  const closeConfirmationModel = () => setIsConfirmationModalOpen(false);

  const openConfirmationModel = (itemName: string, id: string | number) => {
    setItemId(id as number);
    setItemName(itemName);
    setIsConfirmationModalOpen(true);
  };

  const deleteAction = () => {
    closeConfirmationModel();
    allTasksDeleteActionHandler(itemId as number);
  };

  const handleStatus = (value: string) => {
    if (value !== "all") {
      handleQuery("status", value);
    } else {
      handleQuery("status", "");
    }
  };

  const successAction = () => {
    setIsSuccessModalOpen(false);
    handleQuery("page", 1);
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  useEffect(() => {
    if (statusData) {
      const options = statusData.map((item: any) => ({
        label: item?.name || "",
        value: `${item?.id}` || "",
      }));

      setStatusOptions((prev) => [{ label: "All", value: "all" }, ...options]);
    }
  }, [statusGetData]);

  useEffect(() => {
    if (deleteSuccess) {
      setIsSuccessModalOpen(true);
    }

    if (deleteError) {
      setIsErrorModalOpen(true);
    }
  }, [deleteSuccess, deleteError]);

  return (
    <>
      <div className="mt-10" id="archiveTaskTable">
        {isConfirmationModalOpen && (
          <DeleteConfirmationModal
            subtitle={itemName}
            isModalOpen={isConfirmationModalOpen}
            onClose={closeConfirmationModel}
            action={deleteAction}
          />
        )}

        <Table
          columns={
            ArchivedTasksListTableColumn(
              t,
              navigate,
              openConfirmationModel,
            ) as any
          }
          rows={data || []}
          headerExtension={{
            leftComponents: [
              <div className="row">
                <div className="col-12 col-lg-4 col-xl-3 m-0 pt-5 px-5">
                  <SelectControllerOptions
                    label={t(
                      "TM_ARCHIVED_TASKS.TASKS_LIST.TABLE.STATUS_FILTER",
                    )}
                    name={"status"}
                    options={statusOptions}
                    actionHandler={handleStatus}
                    defaultValue="all"
                    formMethods={(methods: any) => {
                      setFormMethods(methods);
                    }}
                  />
                </div>
                <div className="col-12 col-lg-4 col-xl-3 m-0 pt-5 px-5">
                  <Text
                    weight="semibold"
                    color="title"
                    size="sm"
                    component="p"
                    className="mb-3"
                  >
                    {t("TM_ARCHIVED_TASKS.TASKS_LIST.TABLE.START_DATE_FILTER")}
                  </Text>
                  <InputDate
                    name={"startDate"}
                    size="sm"
                    onSelectDate={(date) => {
                      handleQuery("startDate", date);
                    }}
                    defaultValue={query?.startDate as string}
                    placeholder={t(
                      "TM_ARCHIVED_TASKS.TASKS_LIST.TABLE.START_DATE_FILTER",
                    )}
                    prefix={<IconCalendar size="20" fill="subtitle2" />}
                    disabled={false}
                    portal={true}
                    controlling
                  />
                </div>

                <div className="col-12 col-lg-4 col-xl-3 m-0 pt-5 px-5">
                  <Text
                    weight="semibold"
                    color="title"
                    size="sm"
                    component="p"
                    className="mb-3"
                  >
                    {t("TM_ARCHIVED_TASKS.TASKS_LIST.TABLE.END_DATE_FILTER")}
                  </Text>
                  <InputDate
                    name={"name"}
                    size="sm"
                    onSelectDate={(date) => {
                      handleQuery("endDate", date);
                    }}
                    defaultValue={query?.endDate as string}
                    placeholder={t(
                      "TM_ARCHIVED_TASKS.TASKS_LIST.TABLE.END_DATE_FILTER",
                    )}
                    prefix={<IconCalendar size="20" fill="subtitle2" />}
                    disabled={false}
                    portal={true}
                  />
                </div>

                <div className="col-12 col-lg-4 col-xl-3 m-0 pt-5 px-5">
                  <Text
                    weight="semibold"
                    color="title"
                    size="sm"
                    component="p"
                    className="mb-3"
                  >
                    {t("TM_ARCHIVED_TASKS.TASKS_LIST.TABLE.UNIT_FILTER_LABEL")}
                  </Text>

                  <DropdownTreeSelect
                    className="unitTree-DropdownTreeSelect"
                    data={unitTreeDataConvert(
                      unitTree[0]?.children || [],
                      language,
                    )}
                    onChange={(currNode) => {
                      setUnitForSearch(currNode.label);
                      handleQuery("unitId", currNode.id as number);
                    }}
                    onAction={(e) => console.log(e)}
                    onNodeToggle={(e) => console.log(e)}
                    mode="radioSelect"
                    clearSearchOnChange={false}
                    texts={{
                      placeholder:
                        unitForSearch ||
                        t(
                          "TM_ARCHIVED_TASKS.TASKS_LIST.TABLE.UNIT_FILTER_PLACEHOLDER",
                        ),
                    }}
                  />
                </div>

                <div className="col-12 col-lg-4 col-xl-3 m-0 pt-5 px-5">
                  <Text
                    weight="semibold"
                    color="title"
                    size="sm"
                    component="p"
                    className="mb-3"
                  >
                    {t("TM_ARCHIVED_TASKS.TASKS_LIST.TABLE.SEARCH_LABEL")}
                  </Text>
                  <InputText
                    name="simple-input"
                    onChange={(e) => {
                      handleQuery("search", e.target.value);
                    }}
                    value={query?.search}
                    prefix={<IconSearch fill="dark" size="20" />}
                    size="sm"
                    placeholder={t("TM_ARCHIVED_TASKS.TASKS_LIST.TABLE.SEARCH")}
                    type="text"
                    controlling
                  />
                </div>

                <div className="col-12 col-lg-4 col-xl-3 m-0 pt-5 px-5">
                  <Text
                    weight="semibold"
                    color="title"
                    size="sm"
                    component="p"
                    className="mb-3"
                  >
                    {t("TM_ARCHIVED_TASKS.TASKS_LIST.TABLE.SEARCH_CLEAR")}
                  </Text>
                  <Button
                    fill="outline"
                    type="warning"
                    size="sm"
                    onClick={() => {
                      setUnitForSearch("");
                      formMethods && formMethods.setValue("status", "all");
                      clearSearch();
                    }}
                    disabled={!hasFilter}
                  >
                    <IconXClose size="18" fill="warning" />
                  </Button>
                </div>
              </div>,
            ],
          }}
          // edit={{
          //   customIcon: <IconEdit01 size={24} fill="subtitle1" />,
          //   onClick: (id) => {
          //     navigate(`${id}`);
          //   },
          // }}
          // remove={{
          //   customIcon: <IconTrash01 size={24} fill="danger" />,
          //   onClick: (id) => {
          //     allTasksDeleteActionHandler(id);
          //   },
          // }}
          pagination={{
            totalPage: Math.ceil(total / size),
            onClick: (currentPage) => {
              handleQuery("page", currentPage);
            },
            activePage: page,
          }}
        />
      </div>
      {/* modals  */}
      {isSuccessModalOpen && (
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          action={successAction}
          subtitle={"You have deleted a task"}
        />
      )}

      {isErrorModalOpen && (
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
        />
      )}
    </>
  );
};
